import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { api, setOnUnauthorized } from '../api/client'

export type AccountStatus = 'pending' | 'active' | 'suspended' | 'cancelled' | null

// s60 — state machine do onboarding em 5 fases.
// ProtectedRoute lê isto para redirigir. Valores possíveis:
//   phase1_ingestion | phase2_linking | phase3_llm_import | phase4_summary
//   | phase5_chat | complete | abandoned | null (ainda não começou)
export type OnboardingPhase =
  | 'phase1_ingestion'
  | 'phase2_linking'
  | 'phase3_llm_import'
  | 'phase4_summary'
  | 'phase5_chat'
  | 'complete'
  | 'abandoned'
  | null

// Fases em que o chat permanece bloqueado e o user deve estar em /onboarding.
const BLOCKING_PHASES: ReadonlyArray<OnboardingPhase> = [
  'phase1_ingestion',
  'phase2_linking',
  'phase3_llm_import',
  'phase4_summary',
]

export function isOnboardingBlocking(phase: OnboardingPhase): boolean {
  // `null` = user acabou de registar e nunca ligou Gmail. A
  // `OnboardingPage` trata este caso (Phase0GoogleGate), logo a rota tem
  // de o bloquear aqui. `ProtectedRoute` tem um carve-out separado
  // (`phaseTreatsAsComplete`) para users legacy com `onboarded_at`
  // stamped mas `onboarding_phase` ainda null — esses passam.
  return phase === null || BLOCKING_PHASES.includes(phase)
}

interface AuthContextType {
  session: Session | null
  user: User | null
  loading: boolean
  onboarded: boolean | null  // null = still loading
  onboardingPhase: OnboardingPhase  // s60 — fase actual do pipeline
  brainReadyAt: string | null      // s60 — timestamp do fim da Fase 2
  accountStatus: AccountStatus  // null = still loading
  statusReason: string | null
  refreshOnboardingStatus: () => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [onboarded, setOnboarded] = useState<boolean | null>(null)
  const [onboardingPhase, setOnboardingPhase] = useState<OnboardingPhase>(null)
  const [brainReadyAt, setBrainReadyAt] = useState<string | null>(null)
  const [accountStatus, setAccountStatus] = useState<AccountStatus>(null)
  const [statusReason, setStatusReason] = useState<string | null>(null)

  const fetchOnboardingStatus = useCallback(async () => {
    try {
      const me = await api.get<{
        onboarded_at: string | null
        onboarding_phase: OnboardingPhase
        brain_ready_at: string | null
        account_status?: AccountStatus
        status_reason?: string | null
      }>('/auth/me')
      setOnboarded(me.onboarded_at !== null)
      setOnboardingPhase(me.onboarding_phase ?? null)
      setBrainReadyAt(me.brain_ready_at ?? null)
      setAccountStatus((me.account_status ?? 'active') as AccountStatus)
      setStatusReason(me.status_reason ?? null)
    } catch {
      // Backend unreachable or error — treat as not onboarded so the app doesn't freeze
      setOnboarded(false)
      setOnboardingPhase(null)
      setBrainReadyAt(null)
      setAccountStatus('active')
    }
  }, [])

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      if (!session) {
        setOnboarded(null)
        setOnboardingPhase(null)
        setBrainReadyAt(null)
        setAccountStatus(null)
        setStatusReason(null)
      }
    })

    // Any 401 from the backend (= token rotted despite Supabase usually
    // auto-refreshing) means we have to re-authenticate. Sign the user
    // out so Supabase reopens the login screen on the next render,
    // instead of leaving them stuck on a cryptic "Sessão expirou" alert.
    setOnUnauthorized(() => {
      supabase.auth.signOut().catch(() => {})
    })

    return () => {
      subscription.unsubscribe()
      setOnUnauthorized(null)
    }
  }, [])

  // Fetch onboarding status once session is ready
  useEffect(() => {
    if (session && onboarded === null) {
      fetchOnboardingStatus()
    }
  }, [session, onboarded, fetchOnboardingStatus])

  const signInWithGoogle = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    })
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    setSession(null)
    setOnboarded(null)
    setOnboardingPhase(null)
    setBrainReadyAt(null)
    setAccountStatus(null)
    setStatusReason(null)
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        loading,
        onboarded,
        onboardingPhase,
        brainReadyAt,
        accountStatus,
        statusReason,
        refreshOnboardingStatus: fetchOnboardingStatus,
        signInWithGoogle,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
